/**
 * CuratedWinders — browseable list of the `winders` catalog collection, which
 * holds winders, safes AND display cases.
 *
 * One collection, not three: they share most of their fields, and the products
 * themselves overlap (Buben & Zörweg build safes with winders inside, behind
 * glass), so `WinderEntry.applianceKind` discriminates rather than separate
 * entities. See that field's doc comment for the full rationale. The collection,
 * the type, the Algolia index and the Storage paths all keep the historical
 * `winder` name; only what the screen SAYS has widened — hence the heading
 * "Curated Appliances", which is the word the code already uses.
 *
 * ⚠️ THE KIND CHIPS RESOLVE, THEY DO NOT READ. Most entries carry no
 * `applianceKind` — absent means winder and no backfill is run — and pre-B1
 * safes are marked only by `isSafe` with no winding fields. Filtering on the raw
 * field showed the RN safe picker an empty catalogue to an owner who had several
 * (watchlock #1026); `resolveApplianceKind` is the shared answer, and its
 * fallback is permanent rather than a migration window.
 *
 * ⚠️ AND THE ROW MUST ARRIVE INTACT FOR THAT TO HELP. Resolving correctly is
 * only half of it: `winderCatalogueQuery` validated the `applianceKind` coming
 * out of Algolia against a hand-written list that predated display cases, so
 * every display case reached this component with its kind already stripped and
 * resolved — correctly, from what it was given — to "winder". The symptom was a
 * header reading `All 37 | Winders 37` and a Kind column reading "Winder +
 * display case" on jewellery boxes with no motor. The classifier was innocent;
 * the transport was lossy. When a kind looks wrong here, check what the row
 * carries before suspecting the ladder or the document.
 *
 * The kind chips are a UNION MULTI-SELECT, not one-of-four. An appliance is
 * frequently several kinds at once, so "safes and display cases" is a question
 * worth being able to ask in one view.
 *
 * Reads the whole catalog from the `watchkeeper_winders` Algolia index in a
 * single request (`getWinderCatalogue`, which falls back to the Firestore
 * collection read when Algolia is unconfigured, failing, or not yet backfilled),
 * then does its search, its deprecated toggle and its column sorts client-side —
 * the same shape the Movement Manufacturers catalogue uses, and the reason none
 * of the UI below changed when the source did. Row → editor-modal flow, plus row
 * selection and a floating bulk-action bar matching the References and Curated
 * Calibres listings.
 *
 * ⚠️ A ROW IS AN INDEX PROJECTION, NEVER A DOCUMENT TO WRITE BACK. `openEdit`
 * hydrates the row to a real Firestore document before the editor opens, which
 * is exactly what `openEditor` in CuratedCalibres does and what
 * `searchCalibres` means by "a NARROWING pass, not an answer".
 *
 * This listing used to claim the opposite — that the projection was COMPLETE, so
 * a row could go straight into the editor. That premise made an Algolia
 * projection load-bearing for WRITES, and it cost real data: `imageStoragePath`
 * was missing from the index, so the editor opened with a blank image field and
 * `saveWinder` — which nulls every optional field absent from its input — then
 * deleted the path from the document. Thirty winders were exposed to that. An
 * index will always lag its collection (a field the indexer has not shipped yet,
 * a doc written seconds ago), so the editor reads the source of truth and the
 * projection stays what it is good at: listing and ranking.
 *
 * The bulk writes below still go to Firestore directly, and need no hydration of
 * their own: they are `updateDoc` FIELD PATCHES naming only the fields they set,
 * so a field missing from the row is a field they leave alone rather than one
 * they clear. The Algolia record is a read replica, and `syncWinderToAlgolia`
 * reindexes each doc as it is written.
 * `load()` after a bulk write can therefore land a beat ahead of the trigger;
 * the Refresh button is the recourse, same as every other Algolia-backed listing
 * in the admin.
 *
 * Supports a deep-link `openWinderId` so other surfaces can jump straight into
 * one winder's editor, parallel to `openModuleId` / `openCalibreId`.
 *
 * Make labels come from `lookup_winder_manufacturers`: winder docs store the
 * make SLUG, so the list resolves it once on mount rather than rendering raw
 * slugs. A slug with no matching lookup value falls back to the slug itself —
 * a lookup value deleted out from under existing winders should be visible, not
 * blank.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  db,
  getWinderCatalogue,
  getWinderById,
  getLookupCollectionFull,
} from '../../services/firebase';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import type { WinderEntry } from '../../types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { LookupFieldSelect } from '../CuratedWatches/LookupFieldSelect';
import { CustomWinderModal } from './CustomWinderModal';
import { BulkWinderAIEnhance } from './BulkWinderAIEnhance';
import { WinderBulkActionBar } from './WinderBulkActionBar';
import { formatTurnsPerDay } from '../../services/winderTurnsPerDay';
import {
  APPLIANCE_KIND_LABELS,
  STORAGE_APPLIANCE_KINDS,
  appearsInApplianceListing,
  entryWinds,
  resolveApplianceKind,
} from '../../services/winderApplianceKind';
import type { StorageApplianceKind } from '../../types';
import toast from 'react-hot-toast';

/**
 * The kinds an entry is BESIDES its primary one.
 *
 * Read off the same listing predicate, so the badges on a row and the segments
 * that row appears under are very nearly the same answer — with one deliberate
 * exception, below.
 *
 * ⚠️ THE WINDER BADGE NEEDS EVIDENCE, NOT MERELY ELIGIBILITY. The listing
 * predicate files an entry with NO declared kind under Winders, on purpose: an
 * extra row is a curator's to ignore, a missing one is an entry they cannot
 * find. Every pre-B1 safe is in that position — `isSafe: true` and nothing
 * else — so reading the predicate straight through rendered them "Safe +
 * winder", with a tooltip promising winding specs, about an appliance the
 * classifier had just called a safe BECAUSE it has no winding fields.
 *
 * A segment asks "might this belong here". A badge asserts. So the winder badge
 * asks `entryWinds`, which is the question it is actually making a claim about;
 * the other kinds keep the listing predicate, because `isSafe` and
 * `hasDisplayCase` are direct answers rather than defaults.
 */
function secondaryKinds(w: WinderEntry): StorageApplianceKind[] {
  const primary = resolveApplianceKind(w);
  return STORAGE_APPLIANCE_KINDS.filter((k) => {
    if (k === primary || !appearsInApplianceListing(w, k)) return false;
    return k === 'winder' ? entryWinds(w) : true;
  });
}

const SECONDARY_KIND_TITLES: Record<StorageApplianceKind, string> = {
  winder: 'Also winds — carries winding specs',
  safe: 'Built into a safe — keeps its primary kind, carries security specs',
  display_case: 'Also a display case — glazed, carries display specs',
};

const WINDERS_COLLECTION = 'winders';

interface CuratedWindersProps {
  /** Deep-link: open this winder's editor on mount. */
  openWinderId?: string;
  onOpenWinderConsumed?: () => void;
}

type SortKey = 'model' | 'make' | 'kind' | 'slotCount' | 'priceTier' | 'updatedAt';

/**
 * Plural labels for the kind chips.
 *
 * Separate from `APPLIANCE_KIND_LABELS`, which is singular because the Kind
 * COLUMN names one row's kind. A filter chip names a set.
 */
const KIND_CHIP_LABELS: Record<StorageApplianceKind, string> = {
  winder: 'Winders',
  safe: 'Safes',
  display_case: 'Display Cases',
};

/**
 * Which kinds the listing is showing — a SET, not one choice.
 *
 * ⚠️ EMPTY MEANS ALL, and the two are deliberately the same state rather than
 * "empty" being a fourth thing to handle. An appliance is frequently more than
 * one kind at once, so a curator auditing safes and display cases together was
 * previously made to look at them one at a time; toggling is the natural verb
 * for a question with overlapping answers. The cost of a set is that it can be
 * emptied, and an empty set filtered strictly would blank a screen the curator
 * only meant to reset — so unticking the last chip lands on the same view as
 * ticking every chip, which is what "All" means and what the chip row shows.
 */
type KindFilter = Set<StorageApplianceKind>;

/** Chip styling. Selected chips take the brand fill, unselected stay outline. */
const kindTabClass = (active: boolean) =>
  `px-3 py-1.5 text-sm transition-colors ${
    active
      ? 'bg-brand-600 text-white font-medium'
      : 'bg-white text-gray-600 hover:bg-gray-50'
  }`;

function tsToMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'object' && v !== null && 'toMillis' in v) {
    return (v as { toMillis: () => number }).toMillis();
  }
  if (v instanceof Date) return v.getTime();
  return 0;
}

function fmtDate(v: unknown): string {
  const ms = tsToMillis(v);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

export function CuratedWinders({
  openWinderId,
  onOpenWinderConsumed,
}: CuratedWindersProps) {
  const [winders, setWinders] = useState<WinderEntry[]>([]);
  const [makeLabels, setMakeLabels] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('model');
  const [sortAsc, setSortAsc] = useState(true);
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [kindFilter, setKindFilter] = useState<KindFilter>(() => new Set());
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<WinderEntry | null>(null);
  /** True while `openEdit` fetches the document behind a clicked row. */
  const [hydrating, setHydrating] = useState(false);

  // Selection + bulk actions.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [aiEnhanceOpen, setAiEnhanceOpen] = useState(false);
  const [fieldModal, setFieldModal] = useState<null | 'country' | 'priceTier'>(null);
  const [fieldValue, setFieldValue] = useState('');

  const makeLabel = useCallback(
    (slug?: string) => (slug ? makeLabels[slug] || slug : ''),
    [makeLabels],
  );

  // No `fromServer` flag any more: the catalogue comes from Algolia with
  // `cacheable: false`, and its Firestore fallback forces a server read of its
  // own, so every call is already as fresh as either source can be.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, makesDoc] = await Promise.all([
        getWinderCatalogue(),
        // Reading the lookup here also AUTO-SEEDS it on a fresh project (see
        // getLookupCollectionFull), so opening this tab is enough to populate
        // lookup_winder_manufacturers — the AdminConfig button is for re-seeding
        // after the seed list grows.
        getLookupCollectionFull('lookup_winder_manufacturers'),
      ]);
      setWinders(rows);
      setMakeLabels(
        Object.fromEntries(
          (makesDoc?.values || [])
            .filter((v) => v.value)
            .map((v) => [v.value, v.displayName || v.value]),
        ),
      );
    } catch (err) {
      console.error('Failed to load winders:', err);
      toast.error('Failed to load appliances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Deep-link: open one winder's editor straight away.
  useEffect(() => {
    if (!openWinderId) return;
    let cancelled = false;
    getWinderById(openWinderId)
      .then((w) => {
        if (cancelled) return;
        if (w) {
          setEditing(w);
          setEditorOpen(true);
        } else {
          toast.error('Appliance not found');
        }
      })
      .catch(() => toast.error('Failed to open appliance'))
      .finally(() => onOpenWinderConsumed?.());
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openWinderId]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = showDeprecated ? [...winders] : winders.filter((w) => !w.isDeprecated);
    /*
     * Kind is RESOLVED, never read off `applianceKind` directly.
     *
     * Most of the catalogue has no `applianceKind` at all — shared-types says
     * absent means winder and that no backfill should be run — and the safes
     * James catalogued before B1 are marked only by `isSafe` with no winding
     * fields. A direct read would put every one of them under Winders and show
     * an empty Safes tab, which is exactly the bug watchlock #1026 fixed on the
     * RN picker. See `resolveApplianceKind`: the fallback is permanent.
     */
    if (kindFilter.size > 0) {
      // UNION, not intersection: ticking Safes and Display Cases asks for
      // everything that is either, which is how a curator reads two ticked
      // boxes. An intersection would answer a question nobody asked and would
      // usually return nothing.
      rows = rows.filter((w) =>
        [...kindFilter].some((kind) => appearsInApplianceListing(w, kind)),
      );
    }
    if (q) {
      rows = rows.filter((w) => {
        const hay = [
          w.model,
          w.displayName,
          makeLabel(w.make),
          w.make,
          w.countryOfManufacture,
          ...(w.aliases || []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }

    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'model':
          cmp = (a.model || '').localeCompare(b.model || '');
          break;
        case 'make':
          cmp = makeLabel(a.make).localeCompare(makeLabel(b.make));
          break;
        case 'kind':
          // Winders before safes ascending, then model, so the All tab reads as
          // two labelled groups rather than an interleave.
          cmp = resolveApplianceKind(a).localeCompare(resolveApplianceKind(b));
          if (cmp === 0) cmp = (a.model || '').localeCompare(b.model || '');
          break;
        case 'slotCount':
          cmp = (a.slotCount ?? 0) - (b.slotCount ?? 0);
          break;
        case 'priceTier':
          cmp = (a.priceTierSlug || '').localeCompare(b.priceTierSlug || '');
          break;
        case 'updatedAt':
          cmp = tsToMillis(a.updatedAt) - tsToMillis(b.updatedAt);
          break;
      }
      return sortAsc ? cmp : -cmp;
    });
    return rows;
  }, [winders, search, sortKey, sortAsc, showDeprecated, kindFilter, makeLabel]);

  /**
   * Catalogue counts per kind, for the segment labels.
   *
   * ⚠️ THESE DO NOT SUM TO `all`, and that is correct rather than a rounding
   * bug: an entry that winds and protects is counted under both. The counts
   * come from the same predicate the filter uses, so a segment's label can
   * never disagree with what opening it shows.
   */
  const kindCounts = useMemo(() => {
    const rows = showDeprecated ? winders : winders.filter((w) => !w.isDeprecated);
    const counts = { all: rows.length } as Record<
      StorageApplianceKind | 'all',
      number
    >;
    for (const kind of STORAGE_APPLIANCE_KINDS) {
      counts[kind] = rows.filter((w) => appearsInApplianceListing(w, kind)).length;
    }
    return counts;
  }, [winders, showDeprecated]);

  /**
   * Whether every kind is ticked — what the All chip reflects.
   *
   * An EMPTY set counts as all, because that is the view it produces. Showing
   * All as unticked while it is displaying the whole catalogue would be the
   * control lying about the screen next to it.
   */
  const allKindsActive =
    kindFilter.size === 0 || kindFilter.size === STORAGE_APPLIANCE_KINDS.length;

  /** Toggle one kind in or out of the active set. */
  const toggleKind = (kind: StorageApplianceKind) =>
    setKindFilter((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      // Ticking one chip out of an empty (= showing everything) set means "just
      // this kind", not "everything plus this kind" — an empty set is a default,
      // not a selection to add to.
      else next.add(kind);
      return next;
    });

  /**
   * All is a shortcut, not a fourth kind.
   *
   * Ticked when some subset is showing, it selects everything — expressed as
   * the empty set, so a fourth kind added to the union is included by
   * definition rather than by whoever remembered to add it here. Ticked when
   * everything is already showing, it clears to the same view; there is no
   * "nothing" state to fall into.
   */
  const toggleAll = () => setKindFilter(new Set());

  const selectedWinders = useMemo(
    () => winders.filter((w) => selectedIds.has(w.id)),
    [winders, selectedIds],
  );

  // Selection is keyed by id, so a row that scrolls out of the filtered view
  // stays selected. Clearing on a filter change would silently drop work the
  // admin had already ticked.
  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((w) => selectedIds.has(w.id));

  const toggleAllVisible = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) for (const w of filtered) next.delete(w.id);
      else for (const w of filtered) next.add(w.id);
      return next;
    });

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  /**
   * Open the editor on the FRESH document, not on the listing row.
   *
   * Mirrors `openEditor` in CuratedCalibres. The row came from Algolia, and the
   * editor's save writes back every field it was given — so handing it a
   * projection is how a field the index does not carry gets deleted from the
   * document. See the ⚠️ note in the module header for the one that did.
   *
   * A row whose document has since been deleted drops out of the list rather
   * than opening an editor on nothing.
   */
  const openEdit = useCallback(async (w: WinderEntry) => {
    setHydrating(true);
    try {
      const full = await getWinderById(w.id);
      if (!full) {
        toast.error(`"${w.displayName || w.model}" no longer exists`);
        setWinders((prev) => prev.filter((row) => row.id !== w.id));
        return;
      }
      setEditing(full);
      setEditorOpen(true);
    } catch (error) {
      console.error('Failed to load winder document:', error);
      toast.error(`Could not load "${w.displayName || w.model}"`);
    } finally {
      setHydrating(false);
    }
  }, []);

  // ---- Bulk writes ------------------------------------------------------------

  /**
   * Set one field across the selection.
   *
   * FILL-EMPTY by design, matching every other bulk admin action: a winder that
   * already has a value for the field is skipped, never overwritten. There is no
   * force-overwrite toggle — destructive single-field edits go through the
   * editor, one winder at a time.
   */
  const applyFieldToSelection = async (field: 'countryOfManufacture' | 'priceTierSlug', value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      toast.error('Pick a value first');
      return;
    }
    setBulkBusy(true);
    let written = 0;
    let skipped = 0;
    let failed = 0;
    try {
      for (const w of selectedWinders) {
        const existing = (w as unknown as Record<string, unknown>)[field];
        if (typeof existing === 'string' && existing.trim()) {
          skipped += 1;
          continue;
        }
        try {
          await updateDoc(doc(db, WINDERS_COLLECTION, w.id), {
            [field]: trimmed,
            updatedAt: serverTimestamp(),
          });
          written += 1;
        } catch (err) {
          console.error(`Bulk ${field} failed for ${w.id}:`, err);
          failed += 1;
        }
      }
      const parts = [`${written} updated`];
      if (skipped) parts.push(`${skipped} already set`);
      if (failed) parts.push(`${failed} failed`);
      if (failed) toast.error(parts.join(', '));
      else toast.success(parts.join(', '));
      await load();
    } finally {
      setBulkBusy(false);
      setFieldModal(null);
      setFieldValue('');
    }
  };

  /**
   * Flip `isDeprecated` across the selection. Unlike the fill-empty field
   * actions above this DOES overwrite: it's a two-state visibility flag, and
   * "un-deprecate the ones I just deprecated" has to work.
   */
  const applyDeprecated = async (deprecated: boolean) => {
    setBulkBusy(true);
    let written = 0;
    let failed = 0;
    try {
      for (const w of selectedWinders) {
        try {
          await updateDoc(doc(db, WINDERS_COLLECTION, w.id), {
            isDeprecated: deprecated,
            updatedAt: serverTimestamp(),
          });
          written += 1;
        } catch (err) {
          console.error(`Bulk deprecate failed for ${w.id}:`, err);
          failed += 1;
        }
      }
      if (failed) toast.error(`${written} updated, ${failed} failed`);
      else
        toast.success(
          `${written} winder${written === 1 ? '' : 's'} ${deprecated ? 'deprecated' : 'restored'}`,
        );
      await load();
    } finally {
      setBulkBusy(false);
    }
  };

  const sortIndicator = (key: SortKey) =>
    sortKey === key ? (sortAsc ? ' ▲' : ' ▼') : '';

  const headerClass =
    'text-left font-medium px-3 py-2 cursor-pointer select-none hover:text-gray-900';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Curated Appliances</h2>
        {/* The filtered count, with the catalogue size behind it — a bare
            "12 total" while three chips are ticked reads as the collection
            having shrunk. Unfiltered, "37 of 37" would be noise, so it isn't
            shown. */}
        <span className="text-sm text-gray-500">
          {filtered.length === winders.length
            ? `${winders.length} total`
            : `${filtered.length} of ${winders.length}`}
        </span>
        {/* One catalogue, three kinds — see WinderEntry.applianceKind for why
            the collection was not split. Counts are PER KIND and respect the
            deprecated toggle, so a chip never promises rows the table then
            filters away; they do not sum to the total, because an appliance
            that winds and displays is counted under both. */}
        <div
          className="inline-flex rounded-lg border border-gray-300 overflow-hidden"
          role="group"
          aria-label="Filter by appliance kind"
        >
          <button
            type="button"
            onClick={toggleAll}
            aria-pressed={allKindsActive}
            className={kindTabClass(allKindsActive)}
          >
            All
            <span className="ml-1.5 text-xs opacity-70">{kindCounts.all}</span>
          </button>
          {STORAGE_APPLIANCE_KINDS.map((kind) => (
            <button
              key={kind}
              type="button"
              onClick={() => toggleKind(kind)}
              aria-pressed={kindFilter.has(kind)}
              className={`${kindTabClass(kindFilter.has(kind))} border-l border-gray-300`}
            >
              {KIND_CHIP_LABELS[kind]}
              <span className="ml-1.5 text-xs opacity-70">{kindCounts[kind]}</span>
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={showDeprecated}
            onChange={(e) => setShowDeprecated(e.target.checked)}
            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Show deprecated
        </label>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search appliances…"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64"
          />
          <Button variant="secondary" size="sm" onClick={() => load()}>
            Refresh
          </Button>
          {/* The editor's own kind selector decides what is being created, so
              this stays one button. Pre-setting it from the active tab would be
              a hidden default on the one field the curator must think about. */}
          <Button variant="primary" size="sm" onClick={openNew}>
            + New entry
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 italic py-8 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-gray-500 italic py-8 text-center border border-dashed border-gray-200 rounded-lg">
          {winders.length === 0
            ? 'Nothing catalogued yet. Create an entry to get started — tick what it is, pick a make from the lookup, add the model, then Suggest with AI.'
            : kindFilter.size === 0
              ? 'Nothing matches your search.'
              : `No ${[...kindFilter]
                  .map((k) => KIND_CHIP_LABELS[k].toLowerCase())
                  .join(' or ')} match your search.`}
        </div>
      ) : (
        <div
          className={`border border-gray-200 rounded-lg overflow-hidden bg-white ${
            // Also stops a second row being clicked while the first is still
            // being fetched, which would open the editor on the wrong winder.
            hydrating ? 'opacity-60 pointer-events-none' : ''
          }`}
        >
          {hydrating && (
            <div className="px-3 py-1.5 text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
              Opening…
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
              <tr>
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    title="Select every winder currently shown"
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                </th>
                <th className={headerClass} onClick={() => toggleSort('model')}>
                  Model{sortIndicator('model')}
                </th>
                <th className={headerClass} onClick={() => toggleSort('make')}>
                  Make{sortIndicator('make')}
                </th>
                <th className={headerClass} onClick={() => toggleSort('kind')}>
                  Kind{sortIndicator('kind')}
                </th>
                <th className={headerClass} onClick={() => toggleSort('slotCount')}>
                  Slots{sortIndicator('slotCount')}
                </th>
                <th className="text-left font-medium px-3 py-2">TPD</th>
                <th className="text-left font-medium px-3 py-2">Power</th>
                <th className={headerClass} onClick={() => toggleSort('priceTier')}>
                  Tier{sortIndicator('priceTier')}
                </th>
                <th className={headerClass} onClick={() => toggleSort('updatedAt')}>
                  Updated{sortIndicator('updatedAt')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((w) => (
                <tr
                  key={w.id}
                  onClick={() => void openEdit(w)}
                  className={`cursor-pointer hover:bg-gray-50 ${
                    w.isDeprecated ? 'opacity-50' : ''
                  }`}
                >
                  <td
                    className="px-3 py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(w.id)}
                      onChange={() => toggleRow(w.id)}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-800">
                    {w.model}
                    {w.isDeprecated && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">
                        deprecated
                      </span>
                    )}
                    {w.aliases && w.aliases.length > 0 && (
                      <span className="ml-2 text-xs text-gray-400">
                        ({w.aliases.slice(0, 2).join(', ')}
                        {w.aliases.length > 2 ? '…' : ''})
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {makeLabel(w.make) || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {APPLIANCE_KIND_LABELS[resolveApplianceKind(w)]}
                    {/* What else it is — the Buben & Zörweg case, where an
                        entry winds AND protects AND displays. Worth showing:
                        these are why a row appears under headings its primary
                        kind does not name, which would otherwise look like a
                        bug to anyone auditing a segment. */}
                    {secondaryKinds(w).map((k) => (
                      <span
                        key={k}
                        className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400"
                        title={SECONDARY_KIND_TITLES[k]}
                      >
                        + {APPLIANCE_KIND_LABELS[k].toLowerCase()}
                      </span>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {w.slotCount ?? <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {formatTurnsPerDay(w) || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {w.powerSourceSlugs && w.powerSourceSlugs.length > 0 ? (
                      w.powerSourceSlugs.join(', ')
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {w.priceTierSlug || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{fmtDate(w.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedIds.size > 0 && (
        <WinderBulkActionBar
          count={selectedIds.size}
          busy={bulkBusy}
          onAiEnhance={() => setAiEnhanceOpen(true)}
          onSetCountry={() => {
            setFieldValue('');
            setFieldModal('country');
          }}
          onSetPriceTier={() => {
            setFieldValue('');
            setFieldModal('priceTier');
          }}
          onSetDeprecated={(d) => void applyDeprecated(d)}
          onClearSelection={() => setSelectedIds(new Set())}
        />
      )}

      <CustomWinderModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        winder={editing}
        onSaved={() => {
          // Keep the editor open (the modal self-manages closing) so a
          // just-created winder can be AI-enriched in place; just refresh.
          void load();
        }}
        onDeleted={() => {
          setEditorOpen(false);
          void load();
        }}
      />

      <BulkWinderAIEnhance
        isOpen={aiEnhanceOpen}
        onClose={() => setAiEnhanceOpen(false)}
        winders={selectedWinders}
        onApplied={() => {
          setAiEnhanceOpen(false);
          setSelectedIds(new Set());
          void load();
        }}
      />

      {/* Set-one-field bulk modal, shared by Country and Price Tier. */}
      <Modal
        isOpen={fieldModal !== null}
        onClose={() => setFieldModal(null)}
        title={
          fieldModal === 'country'
            ? `Set country of manufacture — ${selectedIds.size} selected`
            : `Set price tier — ${selectedIds.size} selected`
        }
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Applies to every selected winder that does <strong>not</strong> already
            have a value for this field. Winders that already have one are skipped,
            never overwritten — change those in the editor.
          </p>
          {fieldModal === 'country' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country of manufacture
              </label>
              <input
                type="text"
                value={fieldValue}
                onChange={(e) => setFieldValue(e.target.value)}
                placeholder="e.g. Germany"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
          ) : (
            <LookupFieldSelect
              label="Price tier"
              value={fieldValue}
              lookupCollection="lookup_winder_price_tiers"
              onChange={setFieldValue}
            />
          )}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={() => setFieldModal(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={bulkBusy}
              disabled={bulkBusy || !fieldValue.trim()}
              onClick={() =>
                void applyFieldToSelection(
                  fieldModal === 'country' ? 'countryOfManufacture' : 'priceTierSlug',
                  fieldValue,
                )
              }
            >
              Apply to {selectedIds.size}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
